import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../firebase/auth";
import "./Navbar.css";

/* ─── Nav links config ────────────────────────────────────── */
const buildNavLinks = () => [
  { label: "Home",  to: "/" },
  { label: "Shop",  to: "/shop" },
  { label: "Admin", to: "/admin", badge: "Staff" },
];

/* ─── Cart icon ───────────────────────────────────────────── */
function CartIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true">
      <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
      <line x1="3" y1="6" x2="21" y2="6"/>
      <path d="M16 10a4 4 0 0 1-8 0"/>
    </svg>
  );
}

/* ─── Hamburger icon ──────────────────────────────────────── */
function HamburgerIcon({ open }) {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
      <line
        x1="3" y1="6"  x2="19" y2="6"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
        className={`ham-line ham-top ${open ? "open" : ""}`}
      />
      <line
        x1="3" y1="11" x2="19" y2="11"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
        className={`ham-line ham-mid ${open ? "open" : ""}`}
      />
      <line
        x1="3" y1="16" x2="19" y2="16"
        stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
        className={`ham-line ham-bot ${open ? "open" : ""}`}
      />
    </svg>
  );
}

/* ─── Main component ──────────────────────────────────────── */
const Navbar = () => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const dispatch  = useDispatch();
  const { user }  = useSelector((state) => state.auth);
  const cartItems = useSelector((state) => state.cart?.items || []);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const drawerRef = useRef(null);

  const navLinks  = buildNavLinks();
  const cartCount = cartItems.reduce((sum, item) => sum + (item.qty || 1), 0);

  /* Scroll shadow */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close drawer on route change */
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  /* Close drawer on outside click */
  useEffect(() => {
    if (!drawerOpen) return;
    const handler = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [drawerOpen]);

  /* Lock body scroll when drawer is open */
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const handleLogout = async () => {
    setDrawerOpen(false);
    await logoutUser(dispatch);
    navigate("/login");
  };

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  return (
    <>
      <header className={`nav ${scrolled ? "nav-scrolled" : ""}`} role="banner">
        <div className="nav-inner">

          {/* ── Logo ── */}
          <Link to="/" className="nav-logo" aria-label="GlossyLuxHair home">
            <img
              src="images/logoo.png"
              alt=""
              className="nav-logo-img"
              width="38"
              height="38"
            />
            <span className="nav-logo-text">GlossyLux<em>Hair</em></span>
          </Link>

          {/* ── Desktop links ── */}
          <nav className="nav-links" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`nav-link ${isActive(link.to) ? "nav-link-active" : ""}`}
              >
                {link.label}
                {link.badge && (
                  <span className="nav-link-badge">{link.badge}</span>
                )}
              </Link>
            ))}
          </nav>

          {/* ── Desktop actions ── */}
          <div className="nav-actions">
            {/* Cart */}
            <Link
              to="/cart"
              className="nav-cart"
              aria-label={`Cart, ${cartCount} item${cartCount !== 1 ? "s" : ""}`}
            >
              <CartIcon />
              {cartCount > 0 && (
                <span className="nav-cart-count" aria-hidden="true">
                  {cartCount > 9 ? "9+" : cartCount}
                </span>
              )}
            </Link>

            {/* Auth */}
            {user ? (
              <div className="nav-user">
                <div className="nav-avatar" aria-hidden="true">
                  {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
                </div>
                <button className="nav-btn nav-btn-ghost" onClick={handleLogout}>
                  Sign out
                </button>
              </div>
            ) : (
              <div className="nav-auth">
                <Link to="/login"    className="nav-btn nav-btn-ghost">Sign in</Link>
                <Link to="/register" className="nav-btn nav-btn-rose">Join free</Link>
              </div>
            )}

            {/* Hamburger — mobile only */}
            <button
              className="nav-hamburger"
              onClick={() => setDrawerOpen((v) => !v)}
              aria-expanded={drawerOpen}
              aria-controls="nav-drawer"
              aria-label={drawerOpen ? "Close menu" : "Open menu"}
            >
              <HamburgerIcon open={drawerOpen} />
            </button>
          </div>

        </div>
      </header>

      {/* ── Mobile drawer backdrop ── */}
      <div
        className={`nav-backdrop ${drawerOpen ? "nav-backdrop-open" : ""}`}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      {/* ── Mobile drawer ── */}
      <aside
        id="nav-drawer"
        ref={drawerRef}
        className={`nav-drawer ${drawerOpen ? "nav-drawer-open" : ""}`}
        aria-label="Mobile navigation"
        aria-hidden={!drawerOpen}
      >
        {/* Drawer header */}
        <div className="nav-drawer-header">
          <span className="nav-drawer-logo">GlossyLux<em>Hair</em></span>
          <button
            className="nav-drawer-close"
            onClick={() => setDrawerOpen(false)}
            aria-label="Close menu"
          >
            ×
          </button>
        </div>

        {/* User profile strip */}
        {user && (
          <div className="nav-drawer-user">
            <div className="nav-drawer-avatar">
              {(user.displayName || user.email || "U").charAt(0).toUpperCase()}
            </div>
            <div className="nav-drawer-userinfo">
              <span className="nav-drawer-username">
                {user.displayName || "My Account"}
              </span>
              <span className="nav-drawer-email">{user.email}</span>
            </div>
          </div>
        )}

        {/* Drawer links */}
        <nav className="nav-drawer-links" aria-label="Mobile navigation links">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              className={`nav-drawer-link ${isActive(link.to) ? "nav-drawer-link-active" : ""}`}
            >
              <span>{link.label}</span>
              {link.badge && (
                <span className="nav-link-badge">{link.badge}</span>
              )}
              <span className="nav-drawer-arrow">→</span>
            </Link>
          ))}

          <Link
            to="/cart"
            className={`nav-drawer-link ${isActive("/cart") ? "nav-drawer-link-active" : ""}`}
          >
            <span>Cart</span>
            {cartCount > 0 && (
              <span className="nav-cart-count nav-cart-count-inline">
                {cartCount > 9 ? "9+" : cartCount}
              </span>
            )}
            <span className="nav-drawer-arrow">→</span>
          </Link>
        </nav>

        {/* Drawer footer */}
        <div className="nav-drawer-footer">
          {user ? (
            <button className="nav-drawer-logout" onClick={handleLogout}>
              Sign out
            </button>
          ) : (
            <div className="nav-drawer-auth">
              <Link to="/login"    className="nav-btn nav-btn-ghost nav-btn-full">Sign in</Link>
              <Link to="/register" className="nav-btn nav-btn-rose nav-btn-full">Join free →</Link>
            </div>
          )}
        </div>
      </aside>
    </>
  );
};

export default Navbar;